import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Badge } from 'react-bootstrap';
import { setTenantModules } from '../../api/api';

const MODULE_DEFS = [
  {
    id: 'examination',
    name: 'ExaminationOS',
    icon: '📝',
    desc: 'Exam project management, Q.Bank generation, and faculty review workflow.',
    requires: [],
    requiredBy: ['teaching_copilot'],
  },
  {
    id: 'teaching_copilot',
    name: 'Teaching Copilot',
    icon: '🎓',
    desc: 'AI lesson planning, teaching material generation, and course analytics.',
    requires: [],
    requiredBy: ['AskOS', 'content_generation', 'assessment_bank'],
  },
  {
    id: 'AskOS',
    name: 'AskOS',
    icon: '💬',
    desc: 'AI student Q&A platform with concept-linked responses and session analytics.',
    requires: ['teaching_copilot'],
    requiredBy: [],
  },
  {
    id: 'content_generation',
    name: 'AI Content Generation',
    icon: '✍️',
    desc: 'Studio pipeline Step 3 — AI-drafted instructional content per unit.',
    requires: ['teaching_copilot'],
    requiredBy: ['assessment_bank'],
  },
  {
    id: 'assessment_bank',
    name: 'Assessment Bank',
    icon: '🚀',
    desc: 'Studio pipeline Step 4 — automated question bank generation.',
    requires: ['teaching_copilot', 'content_generation'],
    requiredBy: [],
  },
];

const TIER_LABELS = {
  '["examination"]': 'ExamOS Only',
  '["examination","teaching_copilot","AskOS"]': 'ExamOS + Copilot + AskOS',
  '["examination","teaching_copilot","AskOS","content_generation","assessment_bank"]': 'Full Suite',
};

function tierLabel(modules) {
  const key = JSON.stringify([...modules].sort((a, b) => MODULE_DEFS.findIndex(m => m.id === a) - MODULE_DEFS.findIndex(m => m.id === b)));
  return TIER_LABELS[key] || 'Custom';
}

export default function ModuleAssignmentModal({ tenant, show, onHide, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (tenant) {
      setSelected(tenant.modules || ['examination', 'teaching_copilot', 'AskOS', 'content_generation', 'assessment_bank']);
      setMsg('');
    }
  }, [tenant]);

  const toggle = (moduleId) => {
    setSelected((prev) => {
      const def = MODULE_DEFS.find((m) => m.id === moduleId);
      if (prev.includes(moduleId)) {
        // Disabling — also disable anything that requires this module
        const toRemove = new Set([moduleId]);
        MODULE_DEFS.forEach((m) => {
          if (m.requires.some((r) => toRemove.has(r))) toRemove.add(m.id);
        });
        // Run a second pass for cascades (assessment_bank depends on content_generation which depends on teaching_copilot)
        MODULE_DEFS.forEach((m) => {
          if (m.requires.some((r) => toRemove.has(r))) toRemove.add(m.id);
        });
        return prev.filter((id) => !toRemove.has(id));
      } else {
        // Enabling — also enable required modules
        const toAdd = new Set([moduleId, ...(def?.requires || [])]);
        return [...new Set([...prev, ...toAdd])];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      await setTenantModules(tenant.id, selected);
      setMsg('Saved successfully.');
      onSaved && onSaved(selected);
    } catch (e) {
      setMsg('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!tenant) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton className="border-bottom">
        <div>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Module Subscription</Modal.Title>
          <div className="text-muted small mt-1">{tenant.name} · <code style={{ fontSize: 11 }}>{tenant.slug}</code></div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* Tier badge */}
        <div className="px-4 py-3 bg-light border-bottom d-flex align-items-center gap-2">
          <span className="small text-muted fw-semibold">Current tier:</span>
          <Badge bg="primary" className="rounded-pill px-3">{tierLabel(selected)}</Badge>
        </div>

        <div className="p-3">
          {MODULE_DEFS.map((mod) => {
            const isOn = selected.includes(mod.id);
            const isRequired = mod.requires.some((r) => !selected.includes(r));
            return (
              <div
                key={mod.id}
                onClick={() => !isRequired && toggle(mod.id)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '12px 14px', marginBottom: 8,
                  borderRadius: 10, cursor: isRequired ? 'not-allowed' : 'pointer',
                  border: `1.5px solid ${isOn ? '#6366f1' : '#dee2e6'}`,
                  background: isOn ? '#f5f3ff' : '#fff',
                  opacity: isRequired ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{mod.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: isOn ? '#4f46e5' : '#1e293b', marginBottom: 2 }}>
                    {mod.name}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.5 }}>{mod.desc}</div>
                  {mod.requires.length > 0 && (
                    <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4 }}>
                      Requires: {mod.requires.join(', ')}
                    </div>
                  )}
                </div>
                <div style={{
                  width: 38, height: 22, borderRadius: 11, flexShrink: 0, marginTop: 4,
                  background: isOn ? '#6366f1' : '#cbd5e1',
                  position: 'relative', transition: 'background 0.2s',
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 3,
                    left: isOn ? 19 : 3, transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top">
        {msg && (
          <span className={`small fw-semibold me-auto ${msg.startsWith('Error') ? 'text-danger' : 'text-success'}`}>
            {msg}
          </span>
        )}
        <Button variant="outline-secondary" size="sm" onClick={onHide}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving…</> : 'Save Modules'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
